import { useState, useEffect, useCallback } from 'react';
import {
  getCleaningRecords, getHousekeepers, getHousekeepingLeads,
  getProperties, getUnits, getMaintenancePersonnel, getManualCleanings, getDoorCodes
} from '@/lib/api';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { UpcomingCleaningsTab } from '@/components/housekeeping/UpcomingCleaningsTab';
import { HousekeepersTab } from '@/components/housekeeping/HousekeepersTab';
import { HousekeepingLeadsTab } from '@/components/housekeeping/HousekeepingLeadsTab';
import { TenantEditPopup } from '@/components/TenantEditPopup';

export default function HousekeepingPage() {
  const [records, setRecords] = useState([]);
  const [manualCleanings, setManualCleanings] = useState([]);
  const [housekeepers, setHousekeepers] = useState([]);
  const [leads, setLeads] = useState([]);
  const [properties, setProperties] = useState([]);
  const [units, setUnits] = useState([]);
  const [maintenancePersonnel, setMaintenancePersonnel] = useState([]);
  const [doorCodes, setDoorCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showArchived, setShowArchived] = useState(false);
  const [selectedTenantForPopup, setSelectedTenantForPopup] = useState(null);

  const fetchData = useCallback(async (dateRange = null) => {
    try {
      const cleaningParams = dateRange || {};
      const [r, mc, hk, hl, p, u, mp, dc] = await Promise.all([
        getCleaningRecords(cleaningParams),
        getManualCleanings(),
        getHousekeepers({ include_archived: showArchived }),
        getHousekeepingLeads({ include_archived: showArchived }),
        getProperties(),
        getUnits(),
        getMaintenancePersonnel(),
        getDoorCodes()
      ]);
      setRecords(r);
      setManualCleanings(mc);
      setHousekeepers(hk);
      setLeads(hl);
      setProperties(p);
      setUnits(u);
      setMaintenancePersonnel(mp);
      setDoorCodes(dc);
    } catch {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [showArchived]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const unitMap = {};
  units.forEach(u => { unitMap[u.id] = u; });
  const propMap = {};
  properties.forEach(p => { propMap[p.id] = p; });
  const doorCodeMap = {};
  doorCodes.forEach(dc => { doorCodeMap[dc.unit_id] = dc; });

  if (loading) return <div className="py-12 text-center text-muted-foreground text-sm">Loading...</div>;

  return (
    <div className="space-y-4">
      <div>
        <h1 className="font-heading text-2xl font-semibold tracking-tight" data-testid="housekeeping-page-title">
          Housekeeping
        </h1>
        <p className="text-sm text-muted-foreground mt-1">Manage cleanings, housekeepers, and leads</p>
      </div>

      <Tabs defaultValue="upcoming">
        <TabsList data-testid="housekeeping-tabs">
          <TabsTrigger value="upcoming" data-testid="hk-tab-upcoming">Upcoming Cleanings</TabsTrigger>
          <TabsTrigger value="housekeepers" data-testid="hk-tab-housekeepers">Current Housekeepers</TabsTrigger>
          <TabsTrigger value="leads" data-testid="hk-tab-leads">Housekeeping Leads</TabsTrigger>
        </TabsList>

        <TabsContent value="upcoming" className="mt-4">
          <UpcomingCleaningsTab
            records={records}
            manualCleanings={manualCleanings}
            housekeepers={housekeepers.filter(h => !h.is_archived)}
            maintenancePersonnel={maintenancePersonnel}
            unitMap={unitMap}
            propMap={propMap}
            units={units}
            doorCodeMap={doorCodeMap}
            onRefresh={fetchData}
            setSelectedTenantForPopup={setSelectedTenantForPopup}
          />
        </TabsContent>

        <TabsContent value="housekeepers" className="mt-4">
          <HousekeepersTab
            housekeepers={housekeepers}
            showArchived={showArchived}
            setShowArchived={setShowArchived}
            onRefresh={fetchData}
          />
        </TabsContent>

        <TabsContent value="leads" className="mt-4">
          <HousekeepingLeadsTab
            leads={leads}
            showArchived={showArchived}
            setShowArchived={setShowArchived}
            onRefresh={fetchData}
          />
        </TabsContent>
      </Tabs>

      <TenantEditPopup
        open={!!selectedTenantForPopup}
        onClose={() => setSelectedTenantForPopup(null)}
        tenant={selectedTenantForPopup}
        onUpdated={fetchData}
      />
    </div>
  );
}
